import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { SegmentService } from '../../services/segment.service';
import { TreasuryService } from '../../services/treasury.service';
import { CashPosition as CashPositionDto, CashPositionRow } from '../../interfaces/treasury.interface';
import { Segment } from '../../interfaces/accounting.interface';
import { SearchableSelect } from '../common/SearchableSelect';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { parseContentDispositionFilename } from '../../utils/contentDisposition';
import { FILTER_CELL, segmentLabel } from './treasuryRef';

const Tile: React.FC<{ label: string; value: number; testId: string }> = ({ label, value, testId }) => (
  <Card variant="outlined" sx={{ flex: '1 1 200px' }} data-testid={testId}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h6">{formatMoney(value)}</Typography>
    </CardContent>
  </Card>
);

const dash = (n: number | null | undefined) => (n === null || n === undefined ? '—' : formatMoney(n));

const CashPosition: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const [asOf, setAsOf] = useState(toLocalIsoDate());
  const [segmentId, setSegmentId] = useState('');
  const [segments, setSegments] = useState<Segment[]>([]);
  const [data, setData] = useState<CashPositionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await TreasuryService.cashPosition(institutionId, asOf || undefined, segmentId || undefined));
    } catch (e) {
      toast.showApiError(e, 'Failed to load cash position');
    } finally {
      setLoading(false);
    }
  }, [institutionId, asOf, segmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const page = await SegmentService.getByInstitution(0, 200);
        const list: Segment[] = Array.isArray(page) ? page : (page?.content ?? []);
        setSegments(list);
      } catch (e) {
        toast.showApiError(e, 'Failed to load segments');
      }
    })();
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    try {
      setDownloading(true);
      const res = await TreasuryService.cashPositionXlsx(institutionId, asOf || undefined, segmentId || undefined);
      const name = parseContentDispositionFilename(res.headers?.['content-disposition'], `cash-position-${asOf}.xlsx`);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.showApiError(e, 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const bankRows = (data?.rows ?? []).filter((r: CashPositionRow) => r.kind !== 'CASH' && r.kind !== 'FDR');
  const fdrRows = (data?.rows ?? []).filter((r: CashPositionRow) => r.kind === 'FDR');
  const cashRows = (data?.rows ?? []).filter((r: CashPositionRow) => r.kind === 'CASH');

  const renderRow = (r: CashPositionRow, i: number) => (
    <TableRow key={`${r.bankAccountId ?? r.accountId}-${i}`} hover>
      <TableCell>{r.name}{r.accountCode ? <Typography variant="caption" color="text.secondary"> {r.accountCode}</Typography> : null}</TableCell>
      <TableCell>{r.accountType ? humanizeEnum(r.accountType) : '—'}</TableCell>
      <TableCell>{r.currencyCode ?? 'BDT'}</TableCell>
      <TableCell align="right">{formatMoney(r.glBalance)}</TableCell>
      <TableCell align="right">
        {r.foreignMemoBalance != null ? `${r.foreignMemoCurrency ?? ''} ${formatMoney(r.foreignMemoBalance)}` : '—'}
      </TableCell>
      <TableCell align="right">{dash(r.unreconciledDeposits)}</TableCell>
      <TableCell align="right">{dash(r.unreconciledWithdrawals)}</TableCell>
      <TableCell align="right">{dash(r.odLimit)}</TableCell>
      <TableCell align="right">{dash(r.odUtilised)}</TableCell>
      <TableCell align="right">{dash(r.odHeadroom)}</TableCell>
      <TableCell align="right">{dash(r.pdcReceivable7d)}</TableCell>
      <TableCell align="right">{dash(r.pdcPayable7d)}</TableCell>
    </TableRow>
  );

  return (
    <Box data-help-page="banking.cashPosition.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Cash position</Typography>
        <Button data-help="banking.cashPosition.download" variant="outlined" startIcon={<FileDownloadIcon />} disabled={downloading || !data} onClick={download}>
          Download XLSX
        </Button>
      </Box>
      <Box data-help-section="banking.cashPosition.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField data-help="banking.cashPosition.asOf" label="As of" type="date" value={asOf} InputLabelProps={{ shrink: true }} sx={FILTER_CELL}
          onChange={e => setAsOf(e.target.value)} />
        <SearchableSelect<Segment> data-help="banking.cashPosition.segment" label="Segment" options={segments} value={segmentId} getOptionLabel={segmentLabel}
          onChange={id => setSegmentId(id)} sx={FILTER_CELL} />
      </Box>
      {loading && <CircularProgress />}
      {!loading && data && (
        <>
          {data.segmentFiltered && (
            <Chip sx={{ mb: 2 }} color="info" label="Segment view: balances are the segment's share of each account" />
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Tile label="Bank balances" value={data.bankTotal} testId="tile-bank" />
            <Tile label="Cash in hand" value={data.cashInHandTotal} testId="tile-cash" />
            <Tile label="Total (GL, BDT)" value={data.totalGlBalance} testId="tile-total" />
            <Tile label="Cash in transit" value={data.inTransitTotal} testId="tile-transit" />
            <Tile label="Overdraft headroom" value={data.odHeadroomTotal} testId="tile-od" />
            <Tile label="PDC receivable (7 days)" value={data.pdcReceivable7dTotal} testId="tile-pdc-in" />
            <Tile label="PDC payable (7 days)" value={data.pdcPayable7dTotal} testId="tile-pdc-out" />
            <Tile label="FDR principal (outside cash)" value={data.fdrPrincipalTotal ?? 0} testId="tile-fdr-principal" />
            <Tile label="FDRs maturing (30 days)" value={data.fdrMaturing30Total} testId="tile-fdr" />
          </Box>
          {data.pdcReceivableUnassigned7d > 0 && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              PDC receivable not tied to a bank account: {formatMoney(data.pdcReceivableUnassigned7d)}
            </Typography>
          )}
          <TableContainer data-help-section="banking.cashPosition.table" component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="banking.cashPosition.col.account">Account</TableCell><TableCell data-help="banking.cashPosition.col.type">Type</TableCell><TableCell data-help="banking.cashPosition.col.ccy">Ccy</TableCell>
                  <TableCell data-help="banking.cashPosition.col.gl" align="right">GL balance (BDT)</TableCell>
                  <TableCell data-help="banking.cashPosition.col.fcMemo" align="right">FC memo (approx.)</TableCell>
                  <TableCell data-help="banking.cashPosition.col.deposits" align="right">Unreconciled deposits</TableCell>
                  <TableCell data-help="banking.cashPosition.col.withdrawals" align="right">Unreconciled withdrawals</TableCell>
                  <TableCell data-help="banking.cashPosition.col.odLimit" align="right">OD limit</TableCell><TableCell data-help="banking.cashPosition.col.odUsed" align="right">OD used</TableCell>
                  <TableCell data-help="banking.cashPosition.col.odHeadroom" align="right">OD headroom</TableCell>
                  <TableCell data-help="banking.cashPosition.col.pdcIn" align="right">PDC in (7d)</TableCell><TableCell data-help="banking.cashPosition.col.pdcOut" align="right">PDC out (7d)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bankRows.map(renderRow)}
                {cashRows.length > 0 && (
                  <TableRow><TableCell colSpan={12}><Typography variant="subtitle2">Cash in hand</Typography></TableCell></TableRow>
                )}
                {cashRows.map(renderRow)}
                {fdrRows.length > 0 && (
                  <TableRow data-testid="fdr-group-header">
                    <TableCell colSpan={12}>
                      <Typography variant="subtitle2">
                        Fixed deposits (FDR) - principal total {formatMoney(data.fdrPrincipalTotal ?? 0)} (not included in cash totals)
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {fdrRows.map(renderRow)}
                {data.rows.length === 0 && <TableRow><TableCell colSpan={12} align="center">No bank or cash accounts.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 1 }}>FDRs maturing in 30 days</Typography>
          <TableContainer data-help-section="banking.cashPosition.fdrTable" component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead><TableRow><TableCell data-help="banking.cashPosition.fdr.col.number">FDR no.</TableCell><TableCell data-help="banking.cashPosition.fdr.col.bank">Bank</TableCell><TableCell data-help="banking.cashPosition.fdr.col.matures">Matures</TableCell><TableCell data-help="banking.cashPosition.fdr.col.principal" align="right">Principal</TableCell></TableRow></TableHead>
              <TableBody>
                {data.fdrMaturing30.map(f => (
                  <TableRow key={f.id}><TableCell>{f.fdrNumber}</TableCell><TableCell>{f.bankName}</TableCell>
                    <TableCell>{formatDate(f.maturityDate)}</TableCell><TableCell align="right">{formatMoney(f.principal)}</TableCell></TableRow>
                ))}
                {data.fdrMaturing30.length === 0 && <TableRow><TableCell colSpan={4} align="center">None.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 1 }}>Transfers in transit</Typography>
          <TableContainer data-help-section="banking.cashPosition.transitTable" component={Paper}>
            <Table size="small">
              <TableHead><TableRow><TableCell data-help="banking.cashPosition.transit.col.transfer">Transfer</TableCell><TableCell data-help="banking.cashPosition.transit.col.from">From</TableCell><TableCell data-help="banking.cashPosition.transit.col.to">To</TableCell><TableCell data-help="banking.cashPosition.transit.col.sent">Sent</TableCell><TableCell data-help="banking.cashPosition.transit.col.amount" align="right">Amount</TableCell></TableRow></TableHead>
              <TableBody>
                {data.transfersInTransit.map(t => (
                  <TableRow key={t.id}><TableCell>{t.transferNumber}</TableCell><TableCell>{t.fromLabel}</TableCell><TableCell>{t.toLabel}</TableCell>
                    <TableCell>{formatDate(t.sentDate)}</TableCell><TableCell align="right">{formatMoney(t.amount)}</TableCell></TableRow>
                ))}
                {data.transfersInTransit.length === 0 && <TableRow><TableCell colSpan={5} align="center">None.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default CashPosition;
